import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "@/i18n/LanguageContext";
import { useEffect } from "react";
import Header from "@/components/header";
import { Loader2, MessageSquare, Reply, ExternalLink } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import AuthRequired from "../../components/auth-required";

interface User {
  id: number;
  displayName?: string;
  email: string;
  avatarUrl?: string;
}

interface Reply {
  id: number;
  content: string;
  createdAt: string;
  user: User;
}

interface Message {
  id: number;
  content: string;
  createdAt: string;
  user: User;
  customPuzzle: {
    id: number;
    title: string;
    imageUrl?: string;
    createdAt: string;
    userId: number;
  };
  replies: Reply[];
}

export default function DiscussionPage() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();

  // Fetch discussions from the API
  const { data: discussions, isLoading, error } = useQuery<Message[]>({
    queryKey: ["/api/userPuzzleInteractions/discussions"],
    retry: 1,
  });

  // Get user's initials for avatar fallback
  const getUserInitials = (user: User): string => {
    if (user.displayName) {
      return user.displayName
        .split(" ")
        .map(name => name[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    }
    return user.email.substring(0, 2).toUpperCase();
  };

  return (
    <AuthRequired>
      <Header />
      <div className="min-h-screen bg-black text-white pt-20">
        <main className="container mx-auto px-4 py-8">
          <div className="space-y-4 max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold mb-6 text-center">
              {t("discussion.title") || "Recent Discussions"}
            </h1>
            <p className="text-gray-400 text-center mb-8">
              {t("discussion.description") || "Latest comments on puzzles you've interacted with"}
            </p>

            {isLoading ? (
              <div className="flex justify-center my-12">
                <Loader2 className="h-12 w-12 animate-spin text-gray-400" />
              </div>
            ) : error ? (
              <div className="bg-red-900/20 border border-red-800 rounded-lg p-6 text-center">
                <p>{t("discussion.error") || "There was an error loading discussions."}</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => window.location.reload()}
                >
                  {t("common.retry") || "Retry"}
                </Button>
              </div>
            ) : discussions && discussions.length > 0 ? (
              <div className="space-y-6">
                {discussions.map((message) => (
                  <Card key={message.id} className="bg-black border-gray-800 hover:border-gray-700 transition-all">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex flex-col md:flex-row md:items-center gap-2">
                          <CardTitle 
                            className="text-lg text-white hover:text-red-500 cursor-pointer transition-colors"
                            onClick={() => navigate(`/puzzles/custom/${message.customPuzzle.id}`)}
                          >
                            {message.customPuzzle.title}
                          </CardTitle>
                          <CardDescription className="text-gray-400 text-sm">
                            {formatDistanceToNow(new Date(message.createdAt), { addSuffix: true })}
                          </CardDescription>
                        </div>
                        
                        {message.customPuzzle.imageUrl && (
                          <div 
                            className="w-16 h-12 rounded-md overflow-hidden flex-shrink-0 cursor-pointer"
                            onClick={() => navigate(`/puzzles/custom/${message.customPuzzle.id}`)}
                          >
                            <img 
                              src={message.customPuzzle.imageUrl} 
                              alt={message.customPuzzle.title}
                              className="w-full h-full object-cover" 
                            />
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="flex items-start space-x-4 mb-4">
                        <Avatar className="w-10 h-10 border border-gray-700">
                          <AvatarImage src={message.user.avatarUrl} />
                          <AvatarFallback className="bg-red-950 text-red-400">
                            {getUserInitials(message.user)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <p className="font-medium">
                              {message.user.displayName || message.user.email.split('@')[0]}
                            </p>
                          </div>
                          <p className="mt-1 text-gray-200">{message.content}</p>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mt-2 text-red-400 hover:text-red-300 gap-1 px-2 py-1 h-auto"
                            onClick={() => navigate(`/puzzles/custom/${message.customPuzzle.id}`)}
                          >
                            <MessageSquare className="h-4 w-4" />
                            {t("discussion.view_puzzle") || "View Puzzle"}
                          </Button>
                        </div>
                      </div>
                      
                      {message.replies.length > 0 && (
                        <div className="pl-14 space-y-4 mt-4 border-l border-gray-800">
                          {message.replies.map((reply) => (
                            <div key={reply.id} className="flex items-start space-x-4">
                              <Avatar className="w-8 h-8 border border-gray-700">
                                <AvatarImage src={reply.user.avatarUrl} />
                                <AvatarFallback className="bg-red-950 text-red-400 text-xs">
                                  {getUserInitials(reply.user)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                  <p className="font-medium text-sm">
                                    {reply.user.displayName || reply.user.email.split('@')[0]}
                                  </p>
                                  <span className="text-gray-500 text-xs">
                                    {formatDistanceToNow(new Date(reply.createdAt), { addSuffix: true })}
                                  </span>
                                </div>
                                <p className="mt-1 text-gray-300 text-sm">{reply.content}</p>
                              </div>
                            </div>
                          ))}
                          
                          {message.replies.length > 3 && (
                            <Button
                              variant="link"
                              size="sm"
                              className="text-gray-400 hover:text-white p-0 h-auto"
                              onClick={() => navigate(`/puzzles/custom/${message.customPuzzle.id}`)}
                            >
                              {t("discussion.view_more_replies", { count: message.replies.length - 3 }) || 
                                `View ${message.replies.length - 3} more ${message.replies.length - 3 === 1 ? 'reply' : 'replies'}`}
                            </Button>
                          )}
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-800">
                        <Badge 
                          variant="outline" 
                          className="bg-transparent border-gray-700 text-gray-400"
                        >
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {message.replies.length} {message.replies.length === 1 ? 'reply' : 'replies'}
                        </Badge>
                        
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-red-400 hover:bg-red-950 hover:text-red-300 border-gray-700 gap-1"
                          onClick={() => navigate(`/puzzles/custom/${message.customPuzzle.id}`)}
                        >
                          <ExternalLink className="h-4 w-4" />
                          {t("discussion.go_to_puzzle") || "Go to Puzzle"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="bg-gray-900/30 border border-gray-800 rounded-lg p-8 text-center">
                <MessageSquare className="h-12 w-12 mx-auto text-gray-500 mb-4" />
                <h3 className="text-xl font-medium mb-2">
                  {t("discussion.no_discussions") || "No discussions found"}
                </h3>
                <p className="text-gray-400 mb-6">
                  {t("discussion.interact_with_puzzles") || 
                    "Interact with puzzles by creating, attempting, or commenting on them to see discussions here."}
                </p>
                <Button onClick={() => navigate("/desafios-cacatais")}>
                  {t("discussion.explore_puzzles") || "Explore Puzzles"}
                </Button>
              </div>
            )}
          </div>
        </main>
      </div>
    </AuthRequired>
  );
}
